import React, { Component } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';

import { isMobileUA } from 'dpl/shared/utils';
import { HEALTH_TESTING_GUIDE_PATH } from 'dpl/shared/constants/urls';
import TooltipWrapper from 'dpl/common/tooltip/components/TooltipWrapper';
import Icon from 'dpl/common/components/Icon';
import Drawer, {
  DrawerItem,
  DrawerItemTitle,
  DrawerItemContent
} from 'dpl/common/components/Drawer';

const IS_MOBILE_UA = isMobileUA();

function BreedHealthTestsItem({ healthTest }) {
  return (
    <div className="BreedHealthTestsItem">
      <div className="row items-stretch">
        <div className="col-4">
          <div className="flex items-center justify-center ph5 bg-near-white h-100">
            <p className="fw-medium pv5 tc">{healthTest.name}</p>
          </div>
        </div>
        <div className="col-8 flex items-center">
          <p className="pv5">{healthTest.description}</p>
        </div>
      </div>
    </div>
  );
}

BreedHealthTestsItem.propTypes = {
  healthTest: PropTypes.shape({
    name: PropTypes.string.isRequired,
    description: PropTypes.string.isRequired
  }).isRequired
};

function MobileBreedHealthTestsItem({ healthTest }) {
  return (
    <DrawerItem itemKey={healthTest.name} className="bb b--moon-gray">
      <DrawerItemTitle>
        <span className="fw-medium tc">{healthTest.name}</span>
      </DrawerItemTitle>
      <DrawerItemContent>
        <div className="mt2">
          <p>{healthTest.description}</p>
        </div>
      </DrawerItemContent>
    </DrawerItem>
  );
}

MobileBreedHealthTestsItem.propTypes = {
  healthTest: PropTypes.shape({
    name: PropTypes.string.isRequired,
    description: PropTypes.string.isRequired
  }).isRequired
};

export default class BreedHealthTestTiers extends Component {
  static propTypes = {
    healthTestsByTier: PropTypes.arrayOf(
      PropTypes.shape({
        title: PropTypes.string.isRequired,
        any: PropTypes.bool.isRequired,
        level: PropTypes.number.isRequired,
        // eslint-disable-next-line react/forbid-prop-types
        healthTestsGroups: PropTypes.array.isRequired
      })
    ).isRequired
  };

  state = {
    selectedTierIdx: 0
  };

  handleTierClick = idx => {
    this.setState({ selectedTierIdx: idx });
  };

  getHealthTestWhenAny() {
    const provisionedGroup = this.props.healthTestsByTier[
      this.state.selectedTierIdx
    ].healthTestsGroups.find(group => group.provisionalDate);

    return {
      name: 'Any health test',
      description: provisionedGroup
        ? `
        Good Dog recognizes programs that do genetic testing or any other
        relevant health testing with the understanding that all breeders will
        add the indicated tests by ${provisionedGroup.provisionalDate}
      `
        : `
        There are no specific required tests for this level. Instead, Good Dog
        recognizes breeders who perform at least one relevant health test on
        their dogs.
      `
    };
  }

  render() {
    const { healthTestsByTier } = this.props;
    const { selectedTierIdx } = this.state;

    const currentHealthTier = healthTestsByTier[selectedTierIdx];
    const anyTest = this.getHealthTestWhenAny();

    return (
      <div className="BreedHealthTestTiers mw-80-xl center">
        <div className="tc mb10-sm mb6">
          <h5 className="heading-5-sm heading-6 mr2 di v-mid">
            Good Dog&apos;s health testing levels
          </h5>
          <TooltipWrapper
            className="lh-1 v-mid"
            minWidth="300px"
            title={
              <p>
                Understanding health tests is really hard, especially when they
                vary so much across breeds and the science is still developing.
                That&apos;s why Good Dog has built a tiered system to help you
                understand the health testing practices of breeding
                programs.&nbsp;
                <a href={HEALTH_TESTING_GUIDE_PATH} className="underline">
                  Learn more
                </a>
              </p>
            }
          >
            <Icon name="help" width="30px" height="30px" />
          </TooltipWrapper>
        </div>
        <div className="flex fw-medium ba-ns bt bb b--light-gray br2 pv2 mb10-sm mb6 right-bleeding-row left-bleeding-row">
          {healthTestsByTier.map(({ title }, idx) => (
            <div
              key={idx}
              className={classnames('ph2 w-100', {
                'bl b--light-gray': idx !== 0
              })}
            >
              <button
                onClick={() => this.handleTierClick(idx)}
                type="button"
                className={classnames(
                  'button bn transition fw-medium f4-sm pv4-sm pv3 w-100',
                  {
                    'bg-blue-500 white': selectedTierIdx === idx,
                    'bg-transparent black-50': selectedTierIdx !== idx
                  }
                )}
              >
                {title}
              </button>
            </div>
          ))}
        </div>
        <div>
          <p className="f4-sm f3 fw-medium tc mb6-sm mb4">
            A breeding program following &quot;{currentHealthTier.title}&quot;
            health testing will report to screening their dogs for:
          </p>
        </div>
        {currentHealthTier.any && (
          <div className="mb6">
            {IS_MOBILE_UA ? (
              <Drawer>
                <MobileBreedHealthTestsItem healthTest={anyTest} />
              </Drawer>
            ) : (
              <BreedHealthTestsItem healthTest={anyTest} />
            )}
          </div>
        )}

        {currentHealthTier.healthTestsGroups.map(
          (group, _idx) =>
            group.healthTests.length > 0 && (
              <div key={_idx}>
                {group.provisionalDate && (
                  <p className="f4-sm f3 fw-medium tc mb6-sm mb4">
                    Good Breeders must complete the following tests by{' '}
                    {group.provisionalDate} to continue to qualify as &quot;
                    {currentHealthTier.title}&quot;:
                  </p>
                )}
                {group.minRequired && (
                  <p className="f4-sm f3 fw-medium tc mb6-sm mb4">
                    {group.minRequired} of the following:
                  </p>
                )}
                <div className="mb6">
                  {IS_MOBILE_UA ? (
                    <Drawer>
                      {group.healthTests.map((test, idx) => (
                        <MobileBreedHealthTestsItem
                          key={idx}
                          healthTest={test}
                        />
                      ))}
                    </Drawer>
                  ) : (
                    group.healthTests.map((test, idx) => (
                      <BreedHealthTestsItem key={idx} healthTest={test} />
                    ))
                  )}
                </div>
              </div>
            )
        )}
      </div>
    );
  }
}
